import Cookies from 'js-cookie';

import { CallMethodEnums } from '@/types/api';
import { SESSION_COOKIE, SessionCookie } from '@/types/cookie';
import {
  AccessOperationResponse,
  ChangeContractAccessRequest,
  Contract,
  ContractAccessResponse,
  ContractsListResponse,
  ContractStatusResponse,
  ContractValidateRes,
  GrantContractAccessRequest,
  MyContractPermissionsResponse,
  RestrictContractRequest,
  RestrictContractResponse,
  TaskStatusResponse
} from '@/types/dataContracts';

import { callJson, executeFetch } from './utils';

const DCM_PREFIX = '/api/proxy/dcm/api/v1';

// FastAPI returns errors as `{ "detail": "..." }`. Extract the human-readable
// message instead of leaking the raw JSON envelope into UI toasts.
const errorMessageFromResponse = async( res: Response, fallback: string ): Promise<string> => {
  let text = '';
  try {
    text = await res.text();
  } catch {
    return fallback;
  }
  if ( !text ) return fallback;
  try {
    const json = JSON.parse( text );
    if ( typeof json?.detail === 'string' ) return json.detail;
    if ( typeof json?.detail?.message === 'string' ) return json.detail.message;
    if ( typeof json?.message === 'string' ) return json.message;
  } catch {
    // Not JSON — fall through to raw text
  }
  return text;
};

export const getContracts = async(): Promise<ContractsListResponse> => {
  return callJson( {
    route: `${ DCM_PREFIX }/contracts`,
    method: CallMethodEnums.GET,
    isProtected: true
  } );
};

export const getContract = async( contractId: string ): Promise<Contract> => {
  return callJson( {
    route: `${ DCM_PREFIX }/contracts/${ contractId }`,
    method: CallMethodEnums.GET,
    isProtected: true
  } );
};

export const getContractStatus = async( contractId: string ): Promise<ContractStatusResponse> => {
  return callJson( {
    route: `${ DCM_PREFIX }/contracts/status/${ contractId }`,
    method: CallMethodEnums.GET,
    isProtected: true
  } );
};

export const deleteContract = async( contractId: string ): Promise<TaskStatusResponse> => {
  return callJson( {
    route: `${ DCM_PREFIX }/contracts/${ contractId }`,
    method: CallMethodEnums.DELETE,
    isProtected: true
  } );
};

export const validateContract = async( file: File ): Promise<ContractValidateRes> => {
  const formData = new FormData();
  formData.append( 'file', file );

  const res = await executeFetch( {
    route: `${ DCM_PREFIX }/contracts/validate_contract`,
    method: CallMethodEnums.POST,
    isProtected: true,
    formData
  } );

  if ( !res.ok ) {
    throw new Error( await errorMessageFromResponse( res, `Validation failed with status ${ res.status }` ) );
  }

  return res.json();
};

export const createContract = async( file: File ): Promise<{ task_id: string; contract_id?: string }> => {
  const sessionCookieStr = Cookies.get( SESSION_COOKIE )?.valueOf();
  const sessionCookie: SessionCookie | undefined = sessionCookieStr ? JSON.parse( sessionCookieStr ) : undefined;

  const formData = new FormData();
  formData.append( 'file', file );
  if ( sessionCookie?.userId ) {
    formData.append( 'submitted_by', sessionCookie.userId );
  }

  const res = await executeFetch( {
    route: `${ DCM_PREFIX }/contracts/create_contract/`,
    method: CallMethodEnums.POST,
    isProtected: true,
    formData
  } );

  if ( !res.ok ) {
    throw new Error( await errorMessageFromResponse( res, `Create contract failed with status ${ res.status }` ) );
  }

  return res.json();
};

export const updateContract = async( contractId: string, file: File ): Promise<{ task_id: string; contract_id?: string }> => {
  const formData = new FormData();
  formData.append( 'file', file );

  const res = await executeFetch( {
    route: `${ DCM_PREFIX }/contracts/${ contractId }`,
    method: CallMethodEnums.PUT,
    isProtected: true,
    formData
  } );

  if ( !res.ok ) {
    throw new Error( await errorMessageFromResponse( res, `Update contract failed with status ${ res.status }` ) );
  }

  return res.json();
};

export const getTaskStatus = async( taskId: string ): Promise<TaskStatusResponse> => {
  return callJson( {
    route: `${ DCM_PREFIX }/tasks/${ taskId }`,
    method: CallMethodEnums.GET,
    isProtected: true
  } );
};

export const getContractCountBySecret = async( secretName: string ): Promise<number> => {
  const res = await callJson<undefined, { count: number }>( {
    route: `${ DCM_PREFIX }/contracts/by-secret/${ encodeURIComponent( secretName ) }/count`,
    method: CallMethodEnums.GET,
    isProtected: true
  } );
  return res.count;
};

// =============================================================================
// AuthZ access management (KANP-1475 / KANP-1730)
//
// Reads return raw FGA tuple state; UI maps grants[] -> role labels via
// roleFromGrants() in ManageAccessSection/utils. Mutations invalidate
// `dataContractsKeys.access(contractId)` so the panel re-fetches within
// the same interaction (no stale UI between grant -> render).
//
// Endpoints encode `principal` (which contains ':' and may contain '|')
// so '/' segments aren't injected into the URL.
// =============================================================================

export const getContractAccess = async( contractId: string ): Promise<ContractAccessResponse> => {
  return callJson( {
    route: `${ DCM_PREFIX }/contracts/${ contractId }/access`,
    method: CallMethodEnums.GET,
    isProtected: true
  } );
};

// KANP-1474: caller's effective gates on one contract. Distinct from
// getContractAccess (which lists who else has access and requires
// can_manage_access). This one is caller-scoped under /me — any
// audience can call it without 403 ('member with no access' returns
// all-False, not 404, so it can't be used as a probe).
export const getMyContractPermissions = async(
  contractId: string
): Promise<MyContractPermissionsResponse> => {
  return callJson( {
    route: `${ DCM_PREFIX }/me/contracts/${ contractId }/permissions`,
    method: CallMethodEnums.GET,
    isProtected: true
  } );
};

export const grantContractAccess = async(
  contractId: string,
  body: GrantContractAccessRequest
): Promise<AccessOperationResponse> => {
  return callJson( {
    route: `${ DCM_PREFIX }/contracts/${ contractId }/access`,
    method: CallMethodEnums.POST,
    isProtected: true,
    body
  } );
};

export const changeContractAccess = async(
  contractId: string,
  principal: string,
  body: ChangeContractAccessRequest
): Promise<AccessOperationResponse> => {
  return callJson( {
    route: `${ DCM_PREFIX }/contracts/${ contractId }/access/${ encodeURIComponent( principal ) }`,
    method: CallMethodEnums.PATCH,
    isProtected: true,
    body
  } );
};

export const revokeContractAccess = async(
  contractId: string,
  principal: string
): Promise<AccessOperationResponse> => {
  return callJson( {
    route: `${ DCM_PREFIX }/contracts/${ contractId }/access/${ encodeURIComponent( principal ) }`,
    method: CallMethodEnums.DELETE,
    isProtected: true
  } );
};

export const setContractRestrict = async(
  contractId: string,
  body: RestrictContractRequest
): Promise<RestrictContractResponse> => {
  return callJson( {
    route: `${ DCM_PREFIX }/contracts/${ contractId }/restrict`,
    method: CallMethodEnums.PATCH,
    isProtected: true,
    body
  } );
};
